import { RunWithDetails, Segment, PlanItem, AidStation } from '../../types'
import { calculateArrivalTime } from '../calculations'

export interface TimelineEvent {
  type: 'segment' | 'aid_station'
  data: any
  distance: number
  cumulativeTime: number
}

export interface ProcessedSegment extends Segment {
  segmentStart: number
  segmentEnd: number
  pace: number
  segmentTravelTime: number
  segmentStopTime: number
}

export interface NutritionTotals {
  carbs_g: number
  fluids_ml: number
  sodium_mg: number
  caffeine_mg: number
}

/**
 * Format time in minutes to HH:MM format
 */
export function formatTime(minutes: number): string {
  if (!minutes || minutes < 0) return '00:00'
  
  const hours = Math.floor(minutes / 60)
  const mins = Math.floor(minutes % 60)
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

/**
 * Format elevation change with visual indicators
 */
export function formatElevation(gain?: number, loss?: number): {
  gain: string
  loss: string
  net: string
} {
  const elevGain = gain ? `+${gain}m` : '-'
  const elevLoss = loss ? `-${loss}m` : '-'
  const elevNet = (gain || 0) - (loss || 0)
  const elevNetDisplay = elevNet > 0 ? `↗ +${elevNet}m` : elevNet < 0 ? `↘ ${elevNet}m` : '→ 0m'
  
  return {
    gain: elevGain,
    loss: elevLoss,
    net: elevNetDisplay
  }
}

/**
 * Build complete timeline with segments and aid stations for compact card
 * Uses the same calculation logic as full plan for consistency
 */
export function buildCompactTimeline(
  segments: Segment[],
  aidStations: AidStation[],
  planItems: PlanItem[],
  calculateSegmentPace: (segment: Segment) => number,
  calibrationProfile?: any
): TimelineEvent[] {
  const timeline: TimelineEvent[] = []

  // First, build segment timeline using same logic as full plan
  let cumulativeDistance = 0
  let cumulativeTime = 0

  segments.forEach((segment, index) => {
    cumulativeDistance += segment.distance_km
    const pace = calculateSegmentPace(segment)
    const segmentTravelTime = pace * segment.distance_km
    const segmentStopTime = segment.planned_stop_min || 0
    const totalSegmentTime = segmentTravelTime + segmentStopTime
    cumulativeTime += totalSegmentTime

    timeline.push({
      type: 'segment',
      data: {
        ...segment,
        segmentStart: cumulativeDistance - segment.distance_km,
        segmentEnd: cumulativeDistance,
        pace,
        segmentTravelTime,
        segmentStopTime
      },
      distance: cumulativeDistance,
      cumulativeTime
    })
  })

  // Then add aid stations, calculating their ETAs using the correct calculateArrivalTime function
  aidStations.forEach(station => {
    // Use the existing calculateArrivalTime function for consistency
    const arrivalTime = calculateArrivalTime(station.at_distance_km, segments, calibrationProfile)

    timeline.push({
      type: 'aid_station',
      data: station,
      distance: station.at_distance_km,
      cumulativeTime: arrivalTime
    })
  })

  // Sort by distance for proper display order
  timeline.sort((a, b) => a.distance - b.distance)

  return timeline
}

/**
 * Format nutrition items for display in compact format
 */
export function formatNutritionItems(
  planItems: PlanItem[],
  prioritizeDrinks: boolean = true
): string[] {
  const nutritionItems: string[] = []
  
  if (prioritizeDrinks) {
    // Add drink items with amounts first (hydration priority)
    planItems.filter(item => item.type === 'drink').forEach(item => {
      const amount = item.fluids_ml ? `${item.fluids_ml}ml` : ''
      nutritionItems.push(amount ? `■ ${item.name} (${amount})` : `■ ${item.name || 'Drink'}`)
    })
    
    // Add food items
    planItems.filter(item => item.type === 'snack').forEach(item => {
      nutritionItems.push(`■ ${item.name || 'Snack'}`)
    })
  } else {
    // Standard format: all items together
    planItems.forEach(item => {
      const amount = item.fluids_ml ? ` (${item.fluids_ml}ml)` : ''
      nutritionItems.push(`■ ${item.name || 'Item'}${amount}`)
    })
  }
  
  return nutritionItems
}

/**
 * Calculate total distance from segments
 */
export function calculateTotalDistance(segments: Segment[]): number {
  return segments.reduce((total, segment) => total + segment.distance_km, 0)
}

/**
 * Calculate total elevation gain/loss from segments
 */
export function calculateTotalElevation(segments: Segment[]): { gain: number; loss: number } {
  return segments.reduce(
    (totals, segment) => ({
      gain: totals.gain + (segment.elev_gain_m || 0),
      loss: totals.loss + (segment.elev_loss_m || 0)
    }),
    { gain: 0, loss: 0 }
  )
}

/**
 * Calculate nutrition totals from plan items
 */
export function calculateNutritionTotals(planItems: PlanItem[]): NutritionTotals {
  return planItems.reduce(
    (totals, item) => ({
      carbs_g: totals.carbs_g + (item.carbs_g || 0),
      fluids_ml: totals.fluids_ml + (item.fluids_ml || 0),
      sodium_mg: totals.sodium_mg + (item.sodium_mg || 0),
      caffeine_mg: totals.caffeine_mg + (item.caffeine_mg || 0)
    }),
    { carbs_g: 0, fluids_ml: 0, sodium_mg: 0, caffeine_mg: 0 }
  )
}

/**
 * Get nutrition items for a specific segment
 */
export function getSegmentNutrition(segmentId: string, planItems: PlanItem[]): NutritionTotals {
  const segmentItems = planItems.filter(item => item.segment_id === segmentId)
  return calculateNutritionTotals(segmentItems)
}

/**
 * Get nutrition items for a specific aid station
 */
export function getAidStationNutrition(aidStationId: string, planItems: PlanItem[]): NutritionTotals {
  const stationItems = planItems.filter(item => item.aid_station_id === aidStationId)
  return calculateNutritionTotals(stationItems)
}

/**
 * Format nutrition per hour rates
 */
export function formatNutritionRate(amount: number, totalTimeHours: number, unit: string): string {
  if (totalTimeHours <= 0) return '0' + unit + '/h'
  return Math.round(amount / totalTimeHours) + unit + '/h'
}

/**
 * Generate document title for PDF metadata
 */
export function generateDocumentTitle(run: RunWithDetails, format: 'full' | 'compact'): string {
  const formatLabel = format === 'full' ? 'Full Plan' : 'Pace Card'
  return `${run.title} - ${formatLabel}`
}

/**
 * Safe division helper to prevent NaN
 */
export function safeDivide(numerator: number, denominator: number, defaultValue: number = 0): number {
  return denominator === 0 ? defaultValue : numerator / denominator
}

/**
 * Truncate text to fit in PDF cells
 */
export function truncateText(text: string, maxLength: number): string {
  return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text
}